<script setup>
// SiteUp (Lia Imobiliária): modal de sugestão de imóveis aberto pelo Copilot da
// conversa. A IA extrai os critérios 1× (salvos na conversa — paginar/navegar não
// reprocessa); o corretor pode editar os critérios e re-pesquisar. Cada imóvel:
// "Inserir na resposta" (mensagem com emojis), "Copiar link" e "Ver no site".
// Clicar no imóvel abre o detalhe (galeria + infos + setas entre imóveis).
// source 'lia' = inventário próprio; 'todas' = radar. Mesma lógica nas duas abas.
import { ref, computed, onMounted } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useAlert } from 'dashboard/composables';
import ImoveisAPI from 'dashboard/api/imoveis';
import PropertyDetail from './PropertyDetail.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  source: { type: String, required: true },
});
const emit = defineEmits(['close']);

const loading = ref(false);
const errorMsg = ref('');
const items = ref([]);
const filters = ref({});
const page = ref(1);
const totalPages = ref(0);
const total = ref(0);
const pending = ref(false);

const detailIndex = ref(null);
const editing = ref(false);
const draft = ref({});

const EDIT_FIELDS = [
  'operacao',
  'tipo',
  'bairro',
  'dormitorios_min',
  'vagas_min',
  'elevador',
  'area_min',
  'valor_min',
  'valor_max',
  'termo',
];

// Faixas de valor p/ os selects De/Até — adaptadas à operação (venda x aluguel).
const VENDA_BRACKETS = [
  100000, 150000, 200000, 250000, 300000, 400000, 500000, 600000, 750000,
  1000000, 1250000, 1500000, 2000000, 3000000,
];
const ALUGUEL_BRACKETS = [
  500, 800, 1000, 1200, 1500, 2000, 2500, 3000, 4000, 5000, 7000, 10000,
];

const valueBrackets = computed(() =>
  String(draft.value.operacao) === 'aluguel' ? ALUGUEL_BRACKETS : VENDA_BRACKETS
);

const bracketLabel = v => {
  if (v >= 1000000) {
    const s = (v / 1000000)
      .toFixed(2)
      .replace(/\.?0+$/, '')
      .replace('.', ',');
    return `R$ ${s} mi`;
  }
  if (v >= 100000 && v % 1000 === 0) return `R$ ${v / 1000} mil`;
  return `R$ ${Number(v).toLocaleString('pt-BR')}`;
};

// Inclui o valor que a IA extraiu como opção, mesmo fora das faixas (não perde o dado).
const optionsWith = current => {
  const list = [...valueBrackets.value];
  const n = Number(current);
  if (current !== '' && current != null && !Number.isNaN(n) && n > 0 && !list.includes(n)) {
    list.push(n);
  }
  return list.sort((a, b) => a - b);
};
const minValueOptions = computed(() => optionsWith(draft.value.valor_min));
const maxValueOptions = computed(() => optionsWith(draft.value.valor_max));

// Tipo/dormitórios/vagas viram select (mais fácil que digitar); preserva o que a IA
// extraiu como opção mesmo fora da lista. Bairro fica com sugestões (datalist).
const TIPO_BASE = [
  { value: 'apartamento', label: 'Apartamento' },
  { value: 'casa', label: 'Casa' },
  { value: 'terreno', label: 'Terreno' },
  { value: 'sala comercial', label: 'Sala comercial' },
  { value: 'chácara', label: 'Chácara/Sítio' },
  { value: 'cobertura', label: 'Cobertura' },
  { value: 'kitinete', label: 'Kitinete' },
  { value: 'loteamento', label: 'Loteamento' },
  { value: 'pavilhão', label: 'Pavilhão' },
];
const tipoOptions = computed(() => {
  const list = [...TIPO_BASE];
  const cur = (draft.value.tipo || '').toString().trim();
  if (cur && !list.some(t => t.value.toLowerCase() === cur.toLowerCase())) {
    list.unshift({ value: cur, label: cur });
  }
  return list;
});
const numOptions = (base, current) => {
  const arr = [...base];
  const n = Number(current);
  if (current !== '' && current != null && !Number.isNaN(n) && n > 0 && !arr.includes(n)) {
    arr.push(n);
  }
  return arr.sort((a, b) => a - b);
};
const dormOptions = computed(() => numOptions([1, 2, 3, 4, 5], draft.value.dormitorios_min));
const vagaOptions = computed(() => numOptions([1, 2, 3, 4], draft.value.vagas_min));
const BAIRROS = [
  'Centro', 'Bela Vista', 'Três Vendas', 'Novo Atlântico', 'Atlântico', 'Esperança',
  'José Bonifácio', 'Koller', 'Ipiranga', 'Linho', 'Boa Vista', 'Espírito Santo',
  'Liberdade', 'Paiol Grande', 'Copas Verdes', 'Cerâmica', 'Cristal', 'São José',
  'Industrial', 'Presidente Vargas', 'Triângulo', 'Fátima', 'Frinape', 'Aeroporto',
];

const close = () => emit('close');

const title = computed(() =>
  props.source === 'lia'
    ? 'Imóveis · Lia Imobiliária'
    : 'Imóveis · Todas as imobiliárias'
);

const formatBRL = value => {
  if (value === null || value === undefined || value === '') return 'Sob consulta';
  return `R$ ${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const cardSpecs = item => {
  const parts = [];
  if (item.dormitorios) parts.push(`${item.dormitorios} dorm`);
  if (item.garagem) parts.push(`${item.garagem} vaga(s)`);
  if (item.elevador) parts.push('elevador');
  const area = item.area || item.area_total;
  if (area) parts.push(`${Math.round(area)}m²`);
  return parts.join(' · ');
};

const specsEmojiLine = item => {
  const parts = [];
  if (item.dormitorios) parts.push(`🛏 ${item.dormitorios} dorm`);
  if (item.garagem) parts.push(`🚗 ${item.garagem} vaga(s)`);
  if (item.elevador) parts.push('🛗 elevador');
  const area = item.area || item.area_total;
  if (area) parts.push(`📐 ${Math.round(area)}m²`);
  return parts.join(' · ');
};

const filtersLabel = computed(() => {
  const f = filters.value || {};
  const parts = [];
  if (f.operacao) parts.push(f.operacao);
  if (f.tipo) parts.push(f.tipo);
  if (f.dormitorios_min) parts.push(`${f.dormitorios_min}+ dorm`);
  if (f.vagas_min) parts.push(`${f.vagas_min}+ vaga(s)`);
  if (f.elevador) parts.push('com elevador');
  if (f.area_min) parts.push(`${Math.round(f.area_min)}m²+`);
  if (f.bairro) parts.push(f.bairro);
  if (f.valor_max) parts.push(`até ${formatBRL(f.valor_max)}`);
  if (f.valor_min) parts.push(`a partir de ${formatBRL(f.valor_min)}`);
  if (f.termo) parts.push(`"${f.termo}"`);
  return parts.join(' · ');
});

const fetchPage = async (target, opts = {}) => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const { data } = await ImoveisAPI.suggest({
      conversationId: props.conversationId,
      source: props.source,
      page: target,
      filters: opts.filters || null,
      reextract: opts.reextract || false,
    });
    items.value = data.items || [];
    filters.value = data.filters || {};
    total.value = data.total || 0;
    totalPages.value = data.total_pages || 0;
    page.value = data.page || target;
    pending.value = Boolean(data.pending_integration);
    return true;
  } catch (error) {
    errorMsg.value =
      error?.response?.data?.error || 'Não foi possível buscar imóveis agora.';
    return false;
  } finally {
    loading.value = false;
  }
};

// Paginação reusa os filtros já resolvidos -> backend não reprocessa a IA.
const goTo = target => {
  if (loading.value) return;
  if (target < 1 || (totalPages.value && target > totalPages.value)) return;
  fetchPage(target, { filters: filters.value });
};

// ---- Edição de critérios -------------------------------------------------
const startEdit = () => {
  const f = filters.value || {};
  draft.value = EDIT_FIELDS.reduce((acc, key) => {
    acc[key] = f[key] ?? '';
    return acc;
  }, {});
  // normaliza tipo p/ casar com as opções do select (valores em minúsculas)
  if (draft.value.tipo) draft.value.tipo = String(draft.value.tipo).toLowerCase();
  editing.value = true;
};
const cancelEdit = () => {
  editing.value = false;
};
const applyEdit = () => {
  const cleaned = {};
  EDIT_FIELDS.forEach(key => {
    const value = draft.value[key];
    // `false` (checkbox elevador desmarcado) também não vira filtro.
    if (value !== '' && value !== null && value !== undefined && value !== false) {
      cleaned[key] = value;
    }
  });
  editing.value = false;
  detailIndex.value = null;
  fetchPage(1, { filters: cleaned });
};
const reanalyze = () => {
  editing.value = false;
  detailIndex.value = null;
  fetchPage(1, { reextract: true });
};

// ---- Detalhe + navegação entre imóveis (atravessa páginas) ---------------
const detailItem = computed(() =>
  detailIndex.value === null ? null : items.value[detailIndex.value] || null
);
const detailHasPrev = computed(
  () => detailIndex.value > 0 || page.value > 1
);
const detailHasNext = computed(
  () =>
    detailIndex.value < items.value.length - 1 || page.value < totalPages.value
);
const openDetail = index => {
  detailIndex.value = index;
};
const closeDetail = () => {
  detailIndex.value = null;
};
const detailPrev = async () => {
  if (loading.value) return;
  if (detailIndex.value > 0) {
    detailIndex.value -= 1;
  } else if (page.value > 1) {
    const ok = await fetchPage(page.value - 1, { filters: filters.value });
    if (ok && items.value.length) detailIndex.value = items.value.length - 1;
  }
};
const detailNext = async () => {
  if (loading.value) return;
  if (detailIndex.value < items.value.length - 1) {
    detailIndex.value += 1;
  } else if (page.value < totalPages.value) {
    const ok = await fetchPage(page.value + 1, { filters: filters.value });
    if (ok && items.value.length) detailIndex.value = 0;
  }
};

// ---- Ações por imóvel ----------------------------------------------------
const messageFor = item => {
  const local = [item.bairro, item.cidade].filter(Boolean).join(', ');
  // Editor é markdown: `\n` simples vira espaço (colava tudo). Cada campo numa
  // linha usa hard break (2 espaços + \n). O título fica em parágrafo próprio
  // (`\n\n`) p/ o conteúdo ser MULTI-PARÁGRAFO → o editor insere como BLOCO (não
  // inline), separando um imóvel do outro ao colar vários em sequência.
  const details = [
    local ? `📍 ${local}` : null,
    specsEmojiLine(item) || null,
    `💰 ${formatBRL(item.valor)}`,
    item.url ? `🔗 ${item.url}` : null,
  ]
    .filter(Boolean)
    .join('  \n');
  return `🏠 *${item.titulo}*\n\n${details}`;
};

const insertItem = item => {
  // messageFor já é multi-parágrafo → inserção em bloco separa os imóveis.
  emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, messageFor(item));
  useAlert('Imóvel inserido na resposta.');
};

const copyLink = async item => {
  try {
    await navigator.clipboard.writeText(item.url || '');
    useAlert('Link copiado.');
  } catch (error) {
    useAlert('Não foi possível copiar o link.');
  }
};

const openSite = item => {
  if (item.url) window.open(item.url, '_blank', 'noopener');
};

onMounted(() => fetchPage(1));
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-0 sm:p-4"
    @click.self="close"
  >
    <!-- SiteUp: mobile = tela cheia (h-full, sem margem) com scroll interno; desktop =
         card centralizado max-h-[88vh]. max-h-[100dvh] evita corte pela barra do navegador. -->
    <div
      class="w-full h-full sm:h-auto max-h-[100dvh] sm:max-h-[88vh] flex flex-col bg-n-solid-2 border border-n-weak rounded-none sm:rounded-2xl shadow-lg overflow-hidden"
      :class="detailItem ? 'sm:max-w-2xl' : 'sm:max-w-md'"
    >
      <PropertyDetail
        v-if="detailItem"
        class="flex-1 min-h-0"
        :item="detailItem"
        :has-prev="detailHasPrev"
        :has-next="detailHasNext"
        @close="closeDetail"
        @prev="detailPrev"
        @next="detailNext"
        @insert="insertItem(detailItem)"
        @copy="copyLink(detailItem)"
        @site="openSite(detailItem)"
      />

      <template v-else>
        <div
          class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">{{ title }}</span>
          <button
            type="button"
            class="text-n-slate-11 hover:text-n-slate-12"
            aria-label="Fechar"
            @click="close"
          >
            <span class="i-ri-close-line size-4" />
          </button>
        </div>

        <!-- SiteUp: header fixo; filtros + lista rolam JUNTOS (em tela pequena o
             formulário precisa ser alcançável). Paginação fica fixa no rodapé. -->
        <div class="flex-1 min-h-0 overflow-y-auto">
        <!-- Critérios: o que a IA entendeu + editar/reanalisar -->
        <div
          v-if="!loading && !pending"
          class="px-4 py-2 border-b border-n-weak"
        >
          <div v-if="!editing" class="flex items-start justify-between gap-2">
            <p class="text-xs text-n-slate-11 flex-1 min-w-0">
              Entendi da conversa:
              <span class="text-n-slate-12 font-medium">{{
                filtersLabel || '—'
              }}</span>
            </p>
            <div class="flex gap-2 flex-none">
              <button
                type="button"
                class="text-xs text-n-brand hover:underline"
                @click="startEdit"
              >
                Editar
              </button>
              <button
                type="button"
                class="text-xs text-n-slate-11 hover:underline"
                @click="reanalyze"
              >
                Reanalisar
              </button>
            </div>
          </div>

          <div v-else class="flex flex-col gap-2">
            <!-- SiteUp: grid items-start (célula NÃO estica -> o ícone centraliza no
                 select, não na linha). Ícone em wrapper flex (inset-y-0 + items-center)
                 = centralização à prova de quirk. Selects com appearance-none + chevron
                 próprio (a setinha nativa ficava bugada/descentralizada). -->
            <div class="grid grid-cols-2 gap-2 items-start">
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-price-tag-line size-3.5" /></span>
                <select
                  v-model="draft.operacao"
                  class="w-full appearance-none text-xs pl-7 pr-7 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Operação</option>
                  <option value="venda">Venda</option>
                  <option value="aluguel">Aluguel</option>
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-n-slate-10"><span class="i-ri-arrow-down-s-line size-3.5" /></span>
              </div>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-building-2-line size-3.5" /></span>
                <select
                  v-model="draft.tipo"
                  class="w-full appearance-none text-xs pl-7 pr-7 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Tipo</option>
                  <option v-for="t in tipoOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-n-slate-10"><span class="i-ri-arrow-down-s-line size-3.5" /></span>
              </div>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-hotel-bed-line size-3.5" /></span>
                <select
                  v-model.number="draft.dormitorios_min"
                  class="w-full appearance-none text-xs pl-7 pr-7 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Dormitórios</option>
                  <option v-for="n in dormOptions" :key="`d${n}`" :value="n">{{ n }}+ dorm.</option>
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-n-slate-10"><span class="i-ri-arrow-down-s-line size-3.5" /></span>
              </div>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-car-line size-3.5" /></span>
                <select
                  v-model.number="draft.vagas_min"
                  class="w-full appearance-none text-xs pl-7 pr-7 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Vagas</option>
                  <option v-for="n in vagaOptions" :key="`v${n}`" :value="n">{{ n }}+ vaga(s)</option>
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-n-slate-10"><span class="i-ri-arrow-down-s-line size-3.5" /></span>
              </div>
              <label
                class="col-span-2 flex items-center gap-2 text-xs text-n-slate-12 px-1 py-1 cursor-pointer"
              >
                <input
                  v-model="draft.elevador"
                  type="checkbox"
                  class="rounded border-n-weak text-n-brand focus:ring-n-brand"
                />
                <span class="i-ri-arrow-up-down-line size-3.5 text-n-slate-10" />
                Somente com elevador
              </label>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-map-pin-line size-3.5" /></span>
                <input
                  v-model="draft.bairro"
                  list="lia-bairros-sugestoes"
                  placeholder="Bairro"
                  class="w-full text-xs pl-7 pr-2 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                />
                <datalist id="lia-bairros-sugestoes">
                  <option v-for="b in BAIRROS" :key="b" :value="b" />
                </datalist>
              </div>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-ruler-line size-3.5" /></span>
                <input
                  v-model="draft.area_min"
                  type="number"
                  min="0"
                  placeholder="Área mín. (m²)"
                  class="w-full text-xs pl-7 pr-2 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </div>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-cash-line size-3.5" /></span>
                <select
                  v-model.number="draft.valor_min"
                  class="w-full appearance-none text-xs pl-7 pr-7 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Valor de…</option>
                  <option v-for="v in minValueOptions" :key="`min-${v}`" :value="v">{{ bracketLabel(v) }}</option>
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-n-slate-10"><span class="i-ri-arrow-down-s-line size-3.5" /></span>
              </div>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-cash-line size-3.5" /></span>
                <select
                  v-model.number="draft.valor_max"
                  class="w-full appearance-none text-xs pl-7 pr-7 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Valor até…</option>
                  <option v-for="v in maxValueOptions" :key="`max-${v}`" :value="v">{{ bracketLabel(v) }}</option>
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-n-slate-10"><span class="i-ri-arrow-down-s-line size-3.5" /></span>
              </div>
              <div class="relative col-span-2">
                <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"><span class="i-ri-search-line size-3.5" /></span>
                <input
                  v-model="draft.termo"
                  placeholder="Palavra-chave (ex.: nome do residencial)"
                  class="w-full text-xs pl-7 pr-2 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </div>
            </div>
            <div class="flex gap-2 justify-end">
              <button
                type="button"
                class="text-xs px-2.5 py-1 rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                @click="cancelEdit"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="text-xs px-2.5 py-1 rounded-md bg-n-brand text-white hover:opacity-90"
                @click="applyEdit"
              >
                Buscar
              </button>
            </div>
          </div>
        </div>

        <div class="p-3">
          <div v-if="loading" class="py-10 text-center text-sm text-n-slate-11">
            Lendo a conversa e buscando imóveis…
          </div>
          <div
            v-else-if="errorMsg"
            class="py-10 text-center text-sm text-n-ruby-11"
          >
            {{ errorMsg }}
          </div>
          <div
            v-else-if="pending"
            class="py-10 text-center text-sm text-n-slate-11"
          >
            A busca em todas as imobiliárias (radar) será ativada em breve.
          </div>
          <div
            v-else-if="!items.length"
            class="py-10 text-center text-sm text-n-slate-11"
          >
            Nenhum imóvel encontrado para o que entendi da conversa.
          </div>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="(item, index) in items"
              :key="item.id"
              class="flex gap-3 border border-n-weak rounded-xl p-2"
            >
              <button
                type="button"
                class="w-16 h-16 flex-none rounded-md bg-n-alpha-2 overflow-hidden flex items-center justify-center"
                aria-label="Ver detalhes"
                @click="openDetail(index)"
              >
                <img
                  v-if="item.foto"
                  :src="item.foto"
                  :alt="item.titulo"
                  class="w-full h-full object-cover"
                />
                <span v-else class="i-ri-image-line size-5 text-n-slate-10" />
              </button>
              <div class="flex-1 min-w-0">
                <div class="flex justify-between gap-2">
                  <button
                    type="button"
                    class="text-sm font-medium text-n-slate-12 line-clamp-1 text-left hover:underline"
                    @click="openDetail(index)"
                  >
                    {{ item.titulo }}
                  </button>
                  <span
                    class="text-sm font-medium text-n-teal-11 whitespace-nowrap"
                    >{{ formatBRL(item.valor) }}</span
                  >
                </div>
                <div class="text-xs text-n-slate-11 mt-0.5 line-clamp-1">
                  {{ [item.bairro, cardSpecs(item)].filter(Boolean).join(' · ') }}
                </div>
                <div class="flex flex-wrap gap-1.5 mt-1.5">
                  <button
                    type="button"
                    class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                    @click="insertItem(item)"
                  >
                    Inserir na resposta
                  </button>
                  <button
                    type="button"
                    class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                    @click="copyLink(item)"
                  >
                    Copiar link
                  </button>
                  <button
                    type="button"
                    class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                    @click="openSite(item)"
                  >
                    Ver no site
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div
          v-if="!loading && !pending && totalPages > 1"
          class="flex items-center justify-between gap-2 px-4 py-2 border-t border-n-weak text-xs text-n-slate-11"
        >
          <button
            type="button"
            class="px-2 py-1 rounded-md border border-n-weak disabled:opacity-50"
            :disabled="page <= 1"
            @click="goTo(page - 1)"
          >
            Anterior
          </button>
          <span>Página {{ page }} de {{ totalPages }} · {{ total }} imóveis</span>
          <button
            type="button"
            class="px-2 py-1 rounded-md border border-n-weak disabled:opacity-50"
            :disabled="page >= totalPages"
            @click="goTo(page + 1)"
          >
            Próxima
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
